// File: src/pages/LandingPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/Landing.css";
import logoWeb from "../assets/BootLoop_Web_Logo.png";

const TALLY_URL = "https://tally.so/r/81ZXdo";

const LandingPage: React.FC = () => {
  const year = useMemo(() => new Date().getFullYear(), []);
  const [menuOpen, setMenuOpen] = useState(false);

  // Load Tally embed script once
  useEffect(() => {
    const scriptId = "tally-embed-script";
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    const load = () => (window as any).Tally?.loadEmbeds?.();

    if (existing) {
      load();
      return;
    }

    const s = document.createElement("script");
    s.id = scriptId;
    s.src = "https://tally.so/widgets/embed.js";
    s.async = true;
    s.onload = load;
    document.body.appendChild(s);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="lp2 lp2--qr">
      {/* ===== HEADER ===== */}
      <header className="lp2__header lp2__header--qr">
        <div className="lp2__container lp2__headerInner">
          <a href="/" className="lp2__brand" aria-label="BootLoop home">
            <img className="lp2__logo" src={logoWeb} alt="BootLoop" />
          </a>

          <nav className="lp2__nav" aria-label="Primary">
            <a className="lp2__navLink" href="#how">
              How it works
            </a>
            <a className="lp2__navLink" href="#safety">
              Safety
            </a>
            <a className="lp2__navCta" href="#join">
              Join waitlist
            </a>
          </nav>

          <button
            className="lp2__burger"
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen ? "true" : "false"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lp2__mobileMenu" role="dialog" aria-label="Mobile menu">
            <a className="lp2__mobileLink" href="#how" onClick={closeMenu}>
              How it works
            </a>
            <a className="lp2__mobileLink" href="#safety" onClick={closeMenu}>
              Safety
            </a>
            <a className="lp2__mobileCta" href="#join" onClick={closeMenu}>
              Join waitlist
            </a>
          </div>
        )}
      </header>

      <main>
        {/* ===== HERO + FORM (ABOVE THE FOLD) ===== */}
        <section className="lp2__hero lp2__hero--qr" id="top">
          <div className="lp2__container lp2__heroGrid lp2__heroGrid--qr">
            <div className="lp2__heroLeft">
              <div className="lp2__pill">UK football parents ⚽</div>

              <h1 className="lp2__h1">
                Stop buying new football boots every season.
              </h1>

              <p className="lp2__sub">
                BootLoop helps parents swap outgrown kids’ boots locally — quick handovers
                at training, simple and fair.
              </p>

              <div className="lp2__bullets">
                <div className="lp2__bullet">⏱ 30-second signup</div>
                <div className="lp2__bullet">📍 Local swaps near you</div>
                <div className="lp2__bullet">💷 Save money, less waste</div>
              </div>

              <div className="lp2__micro">
                Free beta. We’ll only email when BootLoop is live in your area.
              </div>
            </div>

            <div className="lp2__heroRight" id="join">
              <div className="lp2__waitlistCard lp2__waitlistCard--hero">
                <div className="lp2__waitlistHead">
                  <h2 className="lp2__h2 lp2__h2Tight">Join the waitlist</h2>
                  <span className="lp2__badge">free beta</span>
                </div>

                <p className="lp2__lead lp2__leadTight">
                  Get early access when swaps launch near you.
                </p>

                <div className="lp2__tallyShell lp2__tallyShell--hero">
                  <iframe
                    src={`${TALLY_URL}?transparentBackground=1&dynamicHeight=1`}
                    loading="lazy"
                    width="100%"
                    height="0"
                    frameBorder="0"
                    marginHeight={0}
                    marginWidth={0}
                    title="BootLoop waitlist"
                  />
                </div>

                <div className="lp2__fineprint">No spam. Unsubscribe anytime.</div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== MINI HOW IT WORKS (SHORT, QR FRIENDLY) ===== */}
        <section id="how" className="lp2__section lp2__section--compact">
          <div className="lp2__container">
            <h2 className="lp2__h2">How it works</h2>

            <div className="lp2__grid3">
              <div className="lp2__panel">
                <div className="lp2__panelNum">1</div>
                <h3>Join</h3>
                <p>Register interest so we know where to launch next.</p>
              </div>
              <div className="lp2__panel">
                <div className="lp2__panelNum">2</div>
                <h3>List boots</h3>
                <p>Size + condition + a quick photo when your child outgrows them.</p>
              </div>
              <div className="lp2__panel">
                <div className="lp2__panelNum">3</div>
                <h3>Swap locally</h3>
                <p>Agree a handover at training, matchday, or a public place.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SAFETY (SHORT) ===== */}
        <section
          id="safety"
          className="lp2__section lp2__sectionAlt lp2__section--compact"
        >
          <div className="lp2__container">
            <h2 className="lp2__h2">Safety</h2>

            <div className="lp2__grid3">
              <div className="lp2__panel">
                <h3>Meet in public</h3>
                <p>Training, club grounds, or public places — avoid home addresses.</p>
              </div>
              <div className="lp2__panel">
                <h3>Clear condition</h3>
                <p>Simple condition labels so there are no surprises.</p>
              </div>
              <div className="lp2__panel">
                <h3>Local-first</h3>
                <p>Nearby swaps are easier, quicker, and safer.</p>
              </div>
            </div>

            <div className="lp2__centerCta">
              <a className="lp2__primaryBtn" href="#join">
                Join the waitlist
              </a>
            </div>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="lp2__footer">
          <div className="lp2__container lp2__footerInner">
            <div>© {year} BootLoop</div>
            <div className="lp2__footerLinks">
              <a href="#how">How it works</a>
              <a href="#join">Join</a>
            </div>
          </div>
        </footer>
      </main>

      {/* ===== MOBILE STICKY CTA (QR CONVERSION BOOST) ===== */}
      <div className="lp2__stickyCta" role="region" aria-label="Join BootLoop waitlist">
        <div className="lp2__stickyInner">
          <div className="lp2__stickyText">
            <div className="lp2__stickyTitle">Swap boots locally</div>
            <div className="lp2__stickySub">Free beta • 30 sec signup</div>
          </div>
          <a className="lp2__stickyBtn" href="#join">
            Join
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;